import { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Gift, Package as PkgIcon, RefreshCw, Pencil, Trash2 } from "lucide-react";

const PERIODS = [
  { value: "monthly", label: "Mensile", short: "mese" },
  { value: "quarterly", label: "Trimestrale", short: "trim." },
  { value: "annual", label: "Annuale", short: "anno" },
];
const periodShort = (p) => (PERIODS.find((x) => x.value === p)?.short || "mese");
const toDays = (value, unit) => Math.max(0, (unit === "months" ? (+value || 0) * 30 : (+value || 0)) | 0);

export default function Pacchetti() {
  const [data, setData] = useState({ packages: [], subscriptions: [] });
  const [giftcards, setGiftcards] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);

  const [gcOpen, setGcOpen] = useState(false);
  const [gc, setGc] = useState({ initial_amount: 100, recipient: "", client_id: "" });

  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkg, setPkg] = useState({ name: "", price: 0, validity_value: 12, validity_unit: "months", included: {} });
  const [pkgBusy, setPkgBusy] = useState(false);
  const [editPkgId, setEditPkgId] = useState(null);

  const [gcOpenValidity, setGcOpenValidity] = useState({ value: 0, unit: "months" });

  const [subOpen, setSubOpen] = useState(false);
  const [sub, setSub] = useState({ name: "", price: 0, period: "monthly", benefits: "" });
  const [subBusy, setSubBusy] = useState(false);
  const [editSubId, setEditSubId] = useState(null);

  const load = () => { api.get("/packages").then((r) => setData(r.data)); api.get("/giftcards").then((r) => setGiftcards(r.data)); };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const createGc = async () => {
    const client = clients.find((c) => c.id === gc.client_id);
    const vdays = toDays(gcOpenValidity.value, gcOpenValidity.unit);
    const expiry = vdays > 0 ? new Date(Date.now() + vdays * 86400000).toISOString() : null;
    await api.post("/giftcards", { ...gc, expiry, recipient: gc.recipient || (client ? `${client.first_name} ${client.last_name}` : "") });
    toast.success("Gift Card creata"); setGcOpen(false); setGc({ initial_amount: 100, recipient: "", client_id: "" }); setGcOpenValidity({ value: 0, unit: "months" }); load();
  };

  const setPkgQty = (svc, qty) => setPkg((p) => ({ ...p, included: { ...p.included, [svc.id]: { qty: Math.max(0, qty | 0), name: svc.name } } }));

  const openPkgNew = () => { setEditPkgId(null); setPkg({ name: "", price: 0, validity_value: 12, validity_unit: "months", included: {} }); setPkgOpen(true); };
  const openPkgEdit = (p) => {
    const inc = {};
    (p.included || []).forEach((i) => { if (i.service_id) inc[i.service_id] = { qty: i.qty, name: i.name }; });
    const vd = p.validity_days || 365;
    const useMonths = vd % 30 === 0;
    setEditPkgId(p.id);
    setPkg({ name: p.name || "", price: p.price || 0, validity_value: useMonths ? vd / 30 : vd, validity_unit: useMonths ? "months" : "days", included: inc });
    setPkgOpen(true);
  };
  const deletePkg = async (p) => {
    if (!window.confirm(`Eliminare il pacchetto "${p.name}"? Lo storico e i pacchetti già venduti restano invariati.`)) return;
    await api.delete(`/packages/${p.id}`); toast.success("Pacchetto eliminato"); load();
  };

  const createPkg = async () => {
    if (!pkg.name.trim()) { toast.error("Inserisci il nome del pacchetto"); return; }
    const included = Object.entries(pkg.included)
      .filter(([, v]) => v && v.qty > 0)
      .map(([service_id, v]) => ({ service_id, name: v.name, qty: v.qty }));
    if (included.length === 0) { toast.error("Aggiungi almeno un servizio al pacchetto"); return; }
    setPkgBusy(true);
    try {
      const validity_days = toDays(pkg.validity_value, pkg.validity_unit) || 365;
      const payload = { name: pkg.name.trim(), price: Number(pkg.price) || 0, validity_days, included };
      if (editPkgId) { await api.put(`/packages/${editPkgId}`, payload); toast.success("Pacchetto aggiornato"); }
      else { await api.post("/packages", payload); toast.success("Pacchetto creato"); }
      setPkgOpen(false); setEditPkgId(null); setPkg({ name: "", price: 0, validity_value: 12, validity_unit: "months", included: {} }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio pacchetto"); }
    finally { setPkgBusy(false); }
  };

  const openSubNew = () => { setEditSubId(null); setSub({ name: "", price: 0, period: "monthly", benefits: "" }); setSubOpen(true); };
  const openSubEdit = (s) => { setEditSubId(s.id); setSub({ name: s.name || "", price: s.price || 0, period: s.period || "monthly", benefits: (s.benefits || []).join(", ") }); setSubOpen(true); };
  const deleteSub = async (s) => {
    if (!window.confirm(`Eliminare l'abbonamento "${s.name}"? Lo storico e gli abbonamenti già venduti restano invariati.`)) return;
    await api.delete(`/subscriptions/${s.id}`); toast.success("Abbonamento eliminato"); load();
  };

  const createSub = async () => {
    if (!sub.name.trim()) { toast.error("Inserisci il nome dell'abbonamento"); return; }
    const benefits = sub.benefits.split(",").map((b) => b.trim()).filter(Boolean);
    setSubBusy(true);
    try {
      const payload = { name: sub.name.trim(), price: Number(sub.price) || 0, period: sub.period, benefits };
      if (editSubId) { await api.put(`/subscriptions/${editSubId}`, payload); toast.success("Abbonamento aggiornato"); }
      else { await api.post("/subscriptions", payload); toast.success("Abbonamento creato"); }
      setSubOpen(false); setEditSubId(null); setSub({ name: "", price: 0, period: "monthly", benefits: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio abbonamento"); }
    finally { setSubBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Pacchetti & Gift Card" subtitle="Pacchetti, abbonamenti e carte regalo" />
      <Tabs defaultValue="giftcards">
        <TabsList>
          <TabsTrigger value="giftcards" data-testid="tab-giftcards">Gift Card</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages">Pacchetti</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Abbonamenti</TabsTrigger>
        </TabsList>

        <TabsContent value="giftcards" className="mt-4">
          <div className="flex justify-end mb-3"><Button onClick={() => setGcOpen(true)} data-testid="giftcard-new"><Plus className="h-4 w-4 mr-1" /> Nuova Gift Card</Button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {giftcards.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground col-span-full">Nessuna gift card creata.</Card>}
            {giftcards.map((g) => (
              <Card key={g.id} className="p-4 hi-plaque text-[hsl(36_44%_96%)]">
                <div className="flex justify-between items-start"><Gift className="h-6 w-6 text-[hsl(42_60%_52%)]" /><Badge className="bg-[hsl(42_60%_52%)] text-black">{g.code}</Badge></div>
                <div className="mt-4"><div className="text-xs opacity-70">Saldo residuo</div><div className="font-heading text-3xl font-semibold">{eur(g.balance)}</div></div>
                <div className="text-xs opacity-70 mt-2">{g.recipient} · su {eur(g.initial_amount)}</div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="packages" className="mt-4">
          <div className="flex justify-end mb-3"><Button onClick={openPkgNew} data-testid="package-new"><Plus className="h-4 w-4 mr-1" /> Nuovo Pacchetto</Button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.packages.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground col-span-full">Nessun pacchetto creato.</Card>}
            {data.packages.map((p) => (
              <Card key={p.id} className="p-4" data-testid={`package-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2"><PkgIcon className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><span className="font-medium">{p.name}</span></div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openPkgEdit(p)} data-testid={`package-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deletePkg(p)} data-testid={`package-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div className="font-heading text-2xl font-semibold mt-2">{eur(p.price)}</div>
                <div className="text-xs text-muted-foreground mt-1">{(p.included||[]).map((i) => `${i.qty}x ${i.name}`).join(", ")}</div>
                <div className="text-xs text-muted-foreground">Validità {p.validity_days} giorni</div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-4">
          <div className="flex justify-end mb-3"><Button onClick={openSubNew} data-testid="subscription-new"><Plus className="h-4 w-4 mr-1" /> Nuovo Abbonamento</Button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.subscriptions.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground col-span-full">Nessun abbonamento creato.</Card>}
            {data.subscriptions.map((s) => (
              <Card key={s.id} className="p-4" data-testid={`subscription-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2"><RefreshCw className="h-5 w-5 text-[hsl(var(--hi-status-info))]" /><span className="font-medium">{s.name}</span></div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSubEdit(s)} data-testid={`subscription-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deleteSub(s)} data-testid={`subscription-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div className="font-heading text-2xl font-semibold mt-2">{eur(s.price)}<span className="text-sm text-muted-foreground">/{periodShort(s.period)}</span></div>
                <div className="flex flex-wrap gap-1 mt-2">{(s.benefits||[]).map((b) => <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>)}</div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Gift Card */}
      <Dialog open={gcOpen} onOpenChange={setGcOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-2xl">Nuova Gift Card</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Importo (€)</Label><Input type="number" value={gc.initial_amount} onChange={(e) => setGc({ ...gc, initial_amount: +e.target.value })} data-testid="giftcard-amount" /></div>
            <div className="space-y-1.5"><Label>Cliente (opzionale)</Label><Select value={gc.client_id} onValueChange={(v) => setGc({ ...gc, client_id: v })}><SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Destinatario</Label><Input value={gc.recipient} onChange={(e) => setGc({ ...gc, recipient: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Validità <span className="text-muted-foreground font-normal">(opzionale, 0 = nessuna scadenza)</span></Label>
              <div className="flex gap-2">
                <Input type="number" min="0" className="flex-1" value={gcOpenValidity.value} onChange={(e) => setGcOpenValidity({ ...gcOpenValidity, value: e.target.value })} data-testid="giftcard-validity" />
                <Select value={gcOpenValidity.unit} onValueChange={(v) => setGcOpenValidity({ ...gcOpenValidity, unit: v })}>
                  <SelectTrigger className="w-28" data-testid="giftcard-validity-unit"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="days">Giorni</SelectItem><SelectItem value="months">Mesi</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <Button className="w-full" onClick={createGc} data-testid="giftcard-save">Crea Gift Card</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pacchetto */}
      <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto hi-scroll" data-testid="package-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editPkgId ? "Modifica Pacchetto" : "Nuovo Pacchetto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nome pacchetto</Label><Input value={pkg.name} onChange={(e) => setPkg({ ...pkg, name: e.target.value })} placeholder="Es. Pacchetto 5 Pieghe" data-testid="package-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Prezzo (€)</Label><Input type="number" value={pkg.price} onChange={(e) => setPkg({ ...pkg, price: e.target.value })} data-testid="package-price" /></div>
              <div className="space-y-1.5">
                <Label>Validità</Label>
                <div className="flex gap-2">
                  <Input type="number" min="1" className="flex-1" value={pkg.validity_value} onChange={(e) => setPkg({ ...pkg, validity_value: e.target.value })} data-testid="package-validity" />
                  <Select value={pkg.validity_unit} onValueChange={(v) => setPkg({ ...pkg, validity_unit: v })}>
                    <SelectTrigger className="w-28" data-testid="package-validity-unit"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="days">Giorni</SelectItem><SelectItem value="months">Mesi</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Servizi inclusi (imposta la quantità)</Label>
              <div className="max-h-60 overflow-y-auto hi-scroll rounded-lg border border-border divide-y divide-border">
                {services.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nessun servizio disponibile</div>}
                {services.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="text-sm truncate">{s.name}</span>
                    <Input type="number" min="0" className="w-20 h-8" value={pkg.included[s.id]?.qty || 0}
                      onChange={(e) => setPkgQty(s, +e.target.value)} data-testid={`package-qty-${s.id}`} />
                  </div>
                ))}
              </div>
            </div>
            <Button className="w-full" onClick={createPkg} disabled={pkgBusy} data-testid="package-save">{editPkgId ? "Salva modifiche" : "Crea Pacchetto"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Abbonamento */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent data-testid="subscription-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editSubId ? "Modifica Abbonamento" : "Nuovo Abbonamento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nome abbonamento</Label><Input value={sub.name} onChange={(e) => setSub({ ...sub, name: e.target.value })} placeholder="Es. Membership Gold" data-testid="subscription-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Prezzo (€)</Label><Input type="number" value={sub.price} onChange={(e) => setSub({ ...sub, price: e.target.value })} data-testid="subscription-price" /></div>
              <div className="space-y-1.5"><Label>Periodo</Label>
                <Select value={sub.period} onValueChange={(v) => setSub({ ...sub, period: v })}>
                  <SelectTrigger data-testid="subscription-period"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Vantaggi <span className="text-muted-foreground font-normal">(separati da virgola)</span></Label>
              <Input value={sub.benefits} onChange={(e) => setSub({ ...sub, benefits: e.target.value })} placeholder="Es. 1 piega/mese, -10% servizi, priorità prenotazione" data-testid="subscription-benefits" />
            </div>
            <Button className="w-full" onClick={createSub} disabled={subBusy} data-testid="subscription-save">{editSubId ? "Salva modifiche" : "Crea Abbonamento"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
